import {
  base_colorAction,
  base_colorAlert,
  base_colorContentBorder,
  text_colorTextPrimary,
  text_colorTextSecondary,
} from 'ext-ui/style/colors'
import { spacing } from 'ext-ui/style/spacing'
import {
  font_family_weight_regular,
  font_md,
  font_sm,
} from 'ext-ui/style/typography'
import UploadFileFilledErrorIcon from 'ext-ui/svg/icon/UploadFileFilledErrorIcon'
import UploadFileFilledIcon from 'ext-ui/svg/icon/UploadFileFilledIcon'
import React from 'react'
import { useTranslate } from 'src/localization/Trns'
import { ReceiptAttachmentModel } from 'src/models/receiptAttachment.models'
import { ConnectedPlatformStatus } from 'src/models/transaction.models'
import { PostReceiptAttachmentRequest } from 'src/requests/postReceiptAttachment.request'
import { css } from 'styled-components'

import style from './AttachmentListRows.style'
import AttachmentRow, { RecoveredReceiptRowDetails } from './AttachmentRow'
import { statusForAttachment, statusForRow } from './statusForRow'
import WideDropzone from './WideDropzone'

interface AttachmentListRowsProps {
  attachments: ReceiptAttachmentModel[]
  uploadingAttachmentThumbnails?: Record<string, string>
  uploadingAttachmentFileNames?: Record<string, string>
  failedUploadThumbnails?: Record<string, string>
  failedUploadFileNames?: Record<string, string>
  recentlyUploadedAttachmentIds?: Set<string>
  uploadAttachments?: (attachments: PostReceiptAttachmentRequest[]) => void
  deleteAttachment?: (attachmentId: string) => void
  setAttachmentError?: (
    errorMsg: string | React.ReactElement | undefined,
  ) => void
  setErrors?: (onlyOneReceipt: boolean) => void
  integrationName?: string
  integrationLogoSrc?: string
  erpStatusByAttachmentId?: Record<string, ConnectedPlatformStatus>
  showVerificationStatus?: boolean
  isMissing?: boolean
  useInlineAddReceipt?: boolean
  attachmentCountForUploadLimit?: number
  showViewingLabel?: boolean
  selectedAttachmentId?: string
  onSelectAttachment?: (attachmentId: string) => void
  recoveredReceipt?: RecoveredReceiptRowDetails & {
    attachmentId: string
  }
}

const uploadingRowStyle = css`
  display: flex;
  align-items: flex-start;
  padding: ${spacing(2)} 0;
  gap: ${spacing(4)};

  .uploading-text {
    display: flex;
    flex-direction: column;
    gap: 0;
    min-width: 0;
    font-family: ${font_family_weight_regular};

    .filename {
      ${font_md};
      color: ${text_colorTextPrimary};
    }

    .meta {
      ${font_sm};
      color: ${text_colorTextSecondary};
    }

    .progress {
      width: 200px;
      height: 4px;
      border-radius: 4px;
      overflow: hidden;
      background: ${base_colorContentBorder + '99'};
      margin-top: ${spacing(1)};
    }

    .progress-fill {
      height: 100%;
      background: ${base_colorAction};
      width: 40%;
      border-radius: 4px;
      animation: attachment-row-indeterminate 1.2s ease-in-out infinite
        alternate;
      animation-delay: -0.6s;

      @media (prefers-reduced-motion: reduce) {
        animation: none;
      }
    }
  }
`

function UploadingRow({ fileName }: { fileName?: string }): React.ReactElement {
  const translate = useTranslate()
  const loadingLabel = translate('Loading...')
  return (
    <div css={uploadingRowStyle} data-testid="attachmentRow-uploading">
      <div className="thumb-placeholder">
        <UploadFileFilledIcon width={24} height={24} />
      </div>
      <div className="uploading-text">
        <span
          className="filename"
          data-testid="attachmentRow-uploading-filename"
        >
          {fileName ?? translate('Receipt')}
        </span>
        <span className="meta">{loadingLabel}</span>
        <div className="progress" role="progressbar" aria-label={loadingLabel}>
          <div className="progress-fill" />
        </div>
      </div>
    </div>
  )
}

const failedRowStyle = css`
  display: flex;
  align-items: flex-start;
  padding: ${spacing(2)} 0;
  gap: ${spacing(4)};

  .failed-text {
    display: flex;
    flex-direction: column;
    gap: 0;
    min-width: 0;
    font-family: ${font_family_weight_regular};

    .filename {
      ${font_md};
      color: ${base_colorAlert};
    }

    .meta {
      ${font_sm};
      color: ${text_colorTextSecondary};
    }

    .progress {
      width: 200px;
      height: 4px;
      border-radius: 4px;
      overflow: hidden;
      background: ${base_colorAlert};
      margin-top: ${spacing(1)};
    }
  }
`

function FailedUploadRow({
  fileName,
}: {
  fileName?: string
}): React.ReactElement {
  const translate = useTranslate()
  return (
    <div css={failedRowStyle} data-testid="attachmentRow-failed">
      <div className="thumb-placeholder">
        <UploadFileFilledErrorIcon width={24} height={24} />
      </div>
      <div className="failed-text">
        <span className="filename" data-testid="attachmentRow-failed-filename">
          {fileName ?? translate('Receipt')}
        </span>
        <span className="meta">{translate('Failed to upload')}</span>
        <div
          className="progress"
          role="progressbar"
          aria-label={translate('Failed to upload')}
        />
      </div>
    </div>
  )
}

export default function AttachmentListRows(
  props: AttachmentListRowsProps,
): React.ReactElement {
  const {
    attachments,
    uploadingAttachmentThumbnails,
    uploadingAttachmentFileNames,
    failedUploadThumbnails,
    failedUploadFileNames,
    recentlyUploadedAttachmentIds,
    uploadAttachments,
    deleteAttachment,
    setAttachmentError,
    setErrors,
    integrationName,
    integrationLogoSrc,
    erpStatusByAttachmentId,
    showVerificationStatus = true,
    isMissing,
    useInlineAddReceipt,
    attachmentCountForUploadLimit,
    showViewingLabel,
    selectedAttachmentId,
    onSelectAttachment,
    recoveredReceipt,
  } = props

  const uploadingEntries = Object.entries(uploadingAttachmentThumbnails ?? {})
  const sortedAttachments = [...attachments].sort((a, b) => {
    const aIsRecovered = a.id === recoveredReceipt?.attachmentId
    const bIsRecovered = b.id === recoveredReceipt?.attachmentId

    if (aIsRecovered !== bIsRecovered) return aIsRecovered ? -1 : 1

    const dateA = a.createdAt ? new Date(a.createdAt).getTime() : 0
    const dateB = b.createdAt ? new Date(b.createdAt).getTime() : 0
    return dateB - dateA
  })
  const effectiveSelectedAttachmentId =
    selectedAttachmentId ??
    recoveredReceipt?.attachmentId ??
    (showViewingLabel && sortedAttachments.length === 1
      ? sortedAttachments[0]?.id
      : undefined)
  const isRecoveryList = recoveredReceipt !== undefined || showViewingLabel

  return (
    <div
      css={style}
      className={`AttachmentListRows${
        isRecoveryList ? ' AttachmentListRows--recovery' : ''
      }`}
      data-testid="attachmentListRows"
    >
      {uploadingEntries.map(([uploadId]) => (
        <UploadingRow
          key={uploadId}
          fileName={uploadingAttachmentFileNames?.[uploadId]}
        />
      ))}
      {Object.keys(failedUploadThumbnails ?? {}).map((uploadId) => (
        <FailedUploadRow
          key={uploadId}
          fileName={failedUploadFileNames?.[uploadId]}
        />
      ))}
      {sortedAttachments.map((attachment) => {
        const recoveredReceiptDetails =
          recoveredReceipt?.attachmentId === attachment.id
            ? {
                recoveredFileName: recoveredReceipt.recoveredFileName,
                recoveredFrom: recoveredReceipt.recoveredFrom,
                recoveredAtLabel: recoveredReceipt.recoveredAtLabel,
              }
            : undefined
        const erpStatus = erpStatusByAttachmentId?.[attachment.id]
        const rowStatus =
          showVerificationStatus || attachment.reviewStatus !== undefined
            ? statusForAttachment(attachment, {
                erpStatus,
                erpIntegrationName: integrationName,
              })
            : statusForRow({
                erpStatus,
                erpIntegrationName: integrationName,
              })
        const awaitingVerification =
          showVerificationStatus &&
          rowStatus.statusLabel === 'Complete' &&
          !attachment.verification &&
          !erpStatus &&
          recentlyUploadedAttachmentIds?.has(attachment.id)
        const finalStatus = awaitingVerification
          ? statusForRow({
              uploadState: 'verifying',
              uploadType: attachment.uploadType,
            })
          : rowStatus
        return (
          <AttachmentRow
            key={attachment.id}
            attachment={attachment}
            status={finalStatus}
            integrationName={integrationName}
            integrationLogoSrc={integrationLogoSrc}
            deleteAttachment={isRecoveryList ? undefined : deleteAttachment}
            recoveredReceipt={recoveredReceiptDetails}
            showViewingLabel={
              effectiveSelectedAttachmentId === attachment.id &&
              (showViewingLabel || recoveredReceiptDetails !== undefined)
            }
            onSelectAttachment={onSelectAttachment}
          />
        )
      })}
      {uploadAttachments && (
        <div className="dropzone-slot">
          <WideDropzone
            uploadAttachments={uploadAttachments}
            setAttachmentError={setAttachmentError}
            setErrors={setErrors}
            attachmentsLength={
              attachmentCountForUploadLimit ?? attachments.length
            }
            isMissing={isMissing}
            useInlineAddReceipt={useInlineAddReceipt}
          />
        </div>
      )}
    </div>
  )
}
